import { useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { FormattedMessage, useIntl } from "react-intl"
import { toast } from "react-toastify"

import {
  SearchConsole,
  SuggestionGrid,
  CandidateRow,
  CandidateDetailModal,
  SelectionBar,
  SearchingIndicator,
} from "./components"

// Services
import { sourcingSearch, sourcingIngest } from "../../../services/sourcing"

// Redux: pendingCVs is what drives the "Processing" rows on the Candidate CVs
// page. Pushing the freshly ingested ids there means the badge shows up
// instantly, even before the existing `cv-processed` socket event fires.
import { addPendingCVs } from "../../../redux/slices/cvSlice"

// Hooks
import { useTokens } from "../../../hooks/useTokens"

// Constants / utils
import { getProfileId, getFullName } from "./utils"

const DEFAULT_LIMIT = 25

const SourcingPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const { availableTokens, refetch: refetchTokens } = useTokens({})

  const [query, setQuery] = useState("")
  const [options, setOptions] = useState({
    high_freshness: false,
    reveal_emails: false,
    reveal_phones: false,
  })
  // How many profiles to request in a single search. Exposed as a user-
  // controlled selector (10 / 25 / 50 / 100) because recruiters care a lot
  // about balancing coverage vs. credit spend.
  const [pageSize, setPageSize] = useState(DEFAULT_LIMIT)
  const [session, setSession] = useState(null) // { id, results, total, query }
  const [selected, setSelected] = useState(new Set())
  const [inspected, setInspected] = useState(null)
  const [searching, setSearching] = useState(false)
  const [processing, setProcessing] = useState(false)

  // Credits charged per search == one per profile at fast tier.
  const estimatedCredits = pageSize
  const estimatedProfiles = pageSize

  const runSearch = async (overrideQuery) => {
    const finalQuery = (overrideQuery ?? query).trim()
    if (!finalQuery) return

    if (estimatedCredits > availableTokens) {
      toast.error(formatMessage(
        { id: "toast.notEnoughCredits" },
        { count: estimatedCredits }
      ))
      return
    }

    setSearching(true)
    setSelected(new Set())
    try {
      // Sync the visible input when the call originated from a suggestion.
      if (overrideQuery !== undefined) {
        setQuery(overrideQuery)
      }

      const data = await sourcingSearch({
        query: finalQuery,
        options,
        pagination: { limit: pageSize, offset: 0 },
      })

      setSession({
        id: data.session_id,
        results: data.results || [],
        total: data.pagination?.total_estimate ?? data.results?.length ?? 0,
        query: finalQuery,
      })

      // Search call charged tokens server-side; refresh the visible balance.
      await refetchTokens()
    } catch (err) {
      const status = err?.response?.status
      const message = err?.response?.data?.message
      if (status === 402) {
        toast.error(message || formatMessage({ id: "toast.notEnoughCredits" }, { count: estimatedCredits }))
      } else if (status === 429) {
        toast.error(message || formatMessage({ id: "sourcing.toast.searchFailed" }))
      } else {
        toast.error(message || formatMessage({ id: "sourcing.toast.searchFailed" }))
      }
    } finally {
      setSearching(false)
    }
  }

  const toggleSelect = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const handleProcess = async () => {
    if (!session || selected.size === 0) return

    if (selected.size > availableTokens) {
      toast.error(formatMessage(
        { id: "toast.notEnoughCredits" },
        { count: selected.size }
      ))
      return
    }

    setProcessing(true)
    const selectedIds = Array.from(selected)
    try {
      const res = await sourcingIngest({
        session_id: session.id,
        selected_ids: selectedIds,
      })

      // Eagerly push placeholder pending-CV entries so the user sees them
      // already "Processing" the moment we land on /job-match/select-cv.
      // The existing `cv-processed` socket event will swap them out once
      // upstream parsing finishes.
      const pendingPayload = (res?.task_ids?.length ? res.task_ids : selectedIds)
        .map((taskId, i) => {
          const profile = session.results.find((p) => getProfileId(p) === selectedIds[i])
          const name = profile ? getFullName(profile) : ""
          return {
            cvId: taskId,
            file_name: name || `Sourced candidate ${i + 1}`,
            source: "sourcing",
          }
        })
      dispatch(addPendingCVs(pendingPayload))

      toast.success(formatMessage(
        { id: "sourcing.toast.ingested" },
        { count: selectedIds.length }
      ))

      navigate(`/job-match/select-cv?justSourced=${selectedIds.length}`)
    } catch (err) {
      const status = err?.response?.status
      const message = err?.response?.data?.message
      if (status === 404) {
        toast.error(message || formatMessage({ id: "sourcing.toast.sessionExpired" }))
        // Clear the stale session so the user goes back to the empty state.
        setSession(null)
      } else if (status === 402) {
        toast.error(message || formatMessage({ id: "toast.notEnoughCredits" }, { count: selected.size }))
      } else {
        toast.error(message || formatMessage({ id: "sourcing.toast.ingestFailed" }))
      }
    } finally {
      setProcessing(false)
    }
  }

  const inspectedSelected = useMemo(
    () => (inspected ? selected.has(getProfileId(inspected)) : false),
    [inspected, selected]
  )

  const onLanding = !session

  return (
    <div className="sourcing">
      {/* Top "hero" banner — same treatment as `select-cv__banner`. */}
      <div className="select-cv__banner sourcing__banner">
        <h4 className="select-cv__info h4">
          {onLanding ? (
            <>
              <span>
                <FormattedMessage id="sourcing.heading.search" />
              </span>{" "}
              <FormattedMessage id="sourcing.heading.searchSuffix" />
            </>
          ) : (
            <span>
              <FormattedMessage
                id="sourcing.heading.results"
                values={{ count: session.total }}
              />
            </span>
          )}
        </h4>

        {!onLanding && (
          <button
            type="button"
            className="btn ghost btn--small"
            onClick={() => {
              setSession(null)
              setSelected(new Set())
            }}
          >
            <FormattedMessage id="sourcing.newSearch" />
          </button>
        )}
      </div>

      {/* Token meter — mirrors the existing Info component's blue style. */}
      <div className="info">
        <p className="info__text">
          <FormattedMessage
            id="sourcing.tokens.banner"
            values={{ count: availableTokens }}
          />
        </p>
      </div>

      <SearchConsole
        query={query}
        setQuery={setQuery}
        options={options}
        setOptions={setOptions}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onSubmit={() => runSearch()}
        loading={searching}
        estimatedCredits={estimatedCredits}
        estimatedProfiles={estimatedProfiles}
      />

      {searching && <SearchingIndicator />}

      {!searching && onLanding && (
        <SuggestionGrid onPick={(q) => runSearch(q)} />
      )}

      {!searching && !onLanding && (
        <>
          {session.results.length === 0 ? (
            <p className="sourcing__empty">
              <FormattedMessage id="sourcing.empty.error" />
            </p>
          ) : (
            <div className="sourcing__list">
              {session.results.map((profile) => {
                const id = getProfileId(profile)
                return (
                  <CandidateRow
                    key={id}
                    profile={profile}
                    selected={selected.has(id)}
                    onToggle={toggleSelect}
                    onOpen={setInspected}
                  />
                )
              })}
            </div>
          )}
        </>
      )}

      <SelectionBar
        selectedCount={selected.size}
        onClear={() => setSelected(new Set())}
        onProcess={handleProcess}
        processing={processing}
      />

      <CandidateDetailModal
        open={Boolean(inspected)}
        setOpen={(v) => !v && setInspected(null)}
        profile={inspected}
        selected={inspectedSelected}
        onToggle={toggleSelect}
        options={options}
      />
    </div>
  )
}

export default SourcingPage
